import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom"; // Import for accessing URL parameters
import AuthLayout from "../layouts/auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner"; // Import Spinner component
import { cn } from "../lib/utils";
import axios from "axios"; // Assuming you'll use axios for API calls
import { API_BASE_URL } from "../lib/constants";
import { z } from "zod"; // Import zod
import { useTranslation } from "../contexts/I18nProvider";
import { AlertCircle, Check, X } from "lucide-react";

// Define validation schema using zod with enhanced password requirements
const updatePasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long" })
      .regex(/[A-Z]/, { message: "Password must contain an uppercase letter" })
      .regex(/[a-z]/, { message: "Password must contain a lowercase letter" })
      .regex(/\d/, { message: "Password must contain a number" })
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, { message: "Password must contain a special character" }),
    confirmPassword: z.string().min(1, "Confirm Password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

function UpdatePasswordPage() {
  const { t } = useTranslation();
  const location = useLocation();

  // Parse the query string
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token"); // Retrieve the token

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // State for handling errors
  const [success, setSuccess] = useState(""); // State for handling success messages
  const [formErrors, setFormErrors] = useState({
    password: "",
    confirmPassword: "",
  }); // Validation errors state
  const navigate = useNavigate();

  // Password strength checker - same as sign-up form
  const passwordStrength = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(""); // Reset previous errors
    setSuccess(""); // Reset previous success messages
    setFormErrors({ password: "", confirmPassword: "" }); // Clear form errors

    // Validate the form data using zod
    const result = updatePasswordSchema.safeParse({
      password,
      confirmPassword,
    });

    if (!result.success) {
      // If validation fails, update form errors state
      const errors = result.error.format();
      setFormErrors({
        password: errors.password ? errors.password._errors[0] : "",
        confirmPassword: errors.confirmPassword
          ? errors.confirmPassword._errors[0]
          : "",
      });
      setLoading(false);
      return; // Stop the submission if validation fails
    }

    // Submit data if validation passes
    try {
      const response = await axios.post(API_BASE_URL + "/api/auth/update-password", {
        password,
        confirmPassword,
        token, // Include the token in the request body
      });

      if (response.status === 200) {
        setSuccess("Your password has been updated successfully.");
        setTimeout(() => {
          navigate("/get-started?tab=login");
        }, 500);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Failed to update password. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card className={cn("min-w-[25rem]")}>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('pages.updatePassword.title')}</CardTitle>
          <CardDescription>
            {t('pages.updatePassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              {/* Password Field */}
              <div className="space-y-2">
                <Label 
                  htmlFor="password"
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {t('auth.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={cn(
                    "rounded-lg border-gray-200 dark:border-gray-700",
                    formErrors.password && "border-red-500 focus:ring-red-500"
                  )}
                />
                {formErrors.password && (
                  <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formErrors.password}
                  </p>
                )}
                
                {/* Password Strength Checklist */}
                {password && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">Password requirements:</p>
                    <div className="space-y-1 text-xs">
                      <div className={`flex items-center gap-2 ${passwordStrength.minLength ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                        {passwordStrength.minLength ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasUppercase ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                        {passwordStrength.hasUppercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        Contains uppercase letter
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasLowercase ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                        {passwordStrength.hasLowercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        Contains lowercase letter
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasNumber ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                        {passwordStrength.hasNumber ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        Contains number
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.hasSpecialChar ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                        {passwordStrength.hasSpecialChar ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        Contains special character
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label 
                  htmlFor="confirmPassword"
                  className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {t('auth.confirmPassword')}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={cn(
                    "rounded-lg border-gray-200 dark:border-gray-700",
                    formErrors.confirmPassword && "border-red-500 focus:ring-red-500"
                  )}
                />
                {formErrors.confirmPassword && (
                  <p style={{ color: '#EF4444' }} className="text-sm font-medium flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formErrors.confirmPassword}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Spinner /> : t('pages.updatePassword.updateButton')}
              </Button>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} />
                  <p style={{ color: '#EF4444' }} className="text-sm font-medium">
                    {error}
                  </p>
                </div>
              )}

              {success && (
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-400 dark:border-green-600 flex items-center gap-2">
                  <Check className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">
                    {success}
                  </p>
                </div>
              )}

              <div className="text-center text-sm mt-4">
                <a
                  href="/get-started?tab=login"
                  className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium transition-colors underline underline-offset-4"
                >
                  {t('pages.updatePassword.goToLogin')}
                </a>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export default UpdatePasswordPage;
